import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const DashboardPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();

    // Handle subscription success redirect from PayPal
    const subscriptionStatus = searchParams.get('subscription');
    const token = searchParams.get('token'); // PayPal subscription ID

    if (subscriptionStatus === 'success' && token) {
      activateSubscription(token);
    } else if (subscriptionStatus === 'cancelled') {
      toast.error('Subscription was cancelled');
    }
  }, []);

  const fetchData = async () => {
    try {
      // Admins, dispatchers, and supervisors don't need subscriptions
      if (isAdmin) {
        // For admins, redirect to admin panel
        navigate('/admin');
        return;
      }

      const [subRes, billingRes] = await Promise.all([
        api.get('/subscriptions/my-subscription'),
        api.get('/billing/history'),
      ]);

      setSubscription(subRes.data.subscription);
      setBillingHistory(billingRes.data.billingHistory);

      if (subRes.data.subscription?.status === 'active') {
        const connRes = await api.get('/mumble/connection');
        setConnectionInfo(connRes.data.connection);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const activateSubscription = async (subscriptionId) => {
    try {
      await api.post('/subscriptions/activate', { subscriptionId });
      toast.success('Subscription activated successfully!');
      fetchData();
      // Clean up URL
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error('Failed to activate subscription');
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      const response = await api.post('/subscriptions/create', { planId });

      // Redirect to PayPal for approval
      window.location.href = response.data.approvalUrl;
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to create subscription');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      await api.post('/subscriptions/cancel');
      toast.success('Subscription cancelled successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await api.post('/mumble/reset-password');
      toast.success('Password reset successfully!');
      setConnectionInfo({ ...connectionInfo, password: response.data.newPassword });
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div>
      <nav className="navbar">
        <Link to="/" className="navbar-brand">
          🎙️ Steinmetz Mumble
        </Link>
        <div className="navbar-menu">
          <span style={{ color: 'white' }}>Welcome, {user?.fullName}</span>
          {isAdmin && (
            <Link to="/admin" className="btn btn-secondary">
              Admin
            </Link>
          )}
          <button onClick={logout} className="btn btn-secondary">
            Logout
          </button>
        </div>
      </nav>

      <div className="container">
        <h1 style={{ color: 'white', marginBottom: '2rem' }}>Dashboard</h1>

        {!subscription && (
          <div className="card">
            <h2 style={{ color: '#667eea', marginBottom: '1rem' }}>No Active Subscription</h2>
            <p>Subscribe to a plan to get access to the Mumble server.</p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/')}
            >
              View Plans
            </button>
          </div>
        )}

        {subscription && (
          <>
            <div className="card">
              <h2 style={{ color: '#667eea', marginBottom: '1rem' }}>Current Subscription</h2>
              <div className="dashboard-grid">
                <div>
                  <strong>Plan:</strong> {subscription.plan_name}
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  <span className={`badge badge-${subscription.status === 'active' ? 'success' : 'warning'}`}>
                    {subscription.status}
                  </span>
                </div>
                <div>
                  <strong>Price:</strong> ${subscription.price}/month
                </div>
                <div>
                  <strong>Next billing:</strong>{' '}
                  {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                <button
                  className="btn btn-danger"
                  style={{ marginTop: '1rem' }}
                  onClick={handleCancelSubscription}
                >
                  Cancel Subscription
                </button>
              )}
              {subscription.cancel_at_period_end && (
                <p style={{ color: '#ef4444', marginTop: '1rem' }}>
                  Subscription will be cancelled at the end of the billing period.
                </p>
              )}
            </div>

            {connectionInfo && (
              <div className="card">
                <h2 style={{ color: '#667eea', marginBottom: '1rem' }}>Server Connection Information</h2>
                <p style={{ marginBottom: '1rem' }}>
                  Use these credentials to connect to the Mumble server with your client.
                </p>
                <div className="connection-info">
                  <div className="info-row">
                    <span className="label">Server Address:</span>
                    <span className="value">{connectionInfo.serverAddress}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Port:</span>
                    <span className="value">{connectionInfo.serverPort}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Username:</span>
                    <span className="value">{connectionInfo.username}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Password:</span>
                    <span className="value">{connectionInfo.password}</span>
                  </div>
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ marginTop: '1rem' }}
                  onClick={handleResetPassword}
                >
                  Reset Password
                </button>
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <strong>Download Mumble Client:</strong>
                  <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                    <li>
                      <a href="https://www.mumble.info/downloads/" target="_blank" rel="noreferrer" style={{ color: '#667eea' }}>
                        Official Download Page
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        {billingHistory.length > 0 && (
          <div className="card">
            <h2 style={{ color: '#667eea', marginBottom: '1rem' }}>Billing History</h2>
            <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((item) => (
                  <tr key={item.id}>
                    <td>{new Date(item.created_at).toLocaleDateString()}</td>
                    <td>{item.description}</td>
                    <td>${item.amount}</td>
                    <td>
                      <span className={`badge badge-${item.status === 'succeeded' ? 'success' : 'danger'}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
